(() => {
  if (customElements.get('wohnjoy-wishlist-page')) return;
  class WohnjoyWishlistPage extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      this.cards = new Map();
      this.missing = new Set();
      this.list = this.querySelector('[data-wj-wishlist-items]');
      this.sort = this.querySelector('[data-wj-wishlist-sort]');
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-wj-wishlist-remove]');
        if (remove) this.removeItem(remove.closest('[data-product-id]').dataset.productId);
        if (event.target.closest('[data-wj-wishlist-restore]')) this.restoreItem();
        if (event.target.closest('[data-wj-wishlist-retry]')) {
          for (const id of this.missing) this.cards.delete(id);
          this.missing.clear();
          this.load();
        }
      }, { signal: this.events.signal });
      this.sort.addEventListener('change', () => this.render(), { signal: this.events.signal });
      this.addEventListener('wohnjoy:wishlist-variant', () => this.render(), { signal: this.events.signal });
      document.addEventListener('wohnjoy:wishlist-change', event => {
        if (event.detail.source === 'wishlist-page') return;
        if (event.detail.source === 'storage') { this.cards.clear(); this.missing.clear(); }
        this.load();
      }, { signal: this.events.signal });
      this.load();
    }
    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      clearTimeout(this.undoTimer);
      this.ready = false;
    }
    errorStorage(show) {
      this.querySelector('[data-wj-storage-error]').hidden = !show;
    }
    async load() {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const ids = window.WohnjoyWishlist.readIds();
      const wanted = ids.filter(id => !this.cards.has(id) && !this.missing.has(id));
      const loader = this.querySelector('[data-wj-wishlist-loading]');
      const error = this.querySelector('[data-wj-wishlist-error]');
      error.hidden = true;
      loader.hidden = !wanted.length || this.cards.size > 0;
      this.list.setAttribute('aria-busy', String(wanted.length > 0));
      this.render();
      let failed = false;
      const chunks = [];
      for (let start = 0; start < wanted.length; start += 40) chunks.push(wanted.slice(start, start + 40));
      for (let index = 0; index < chunks.length; index += 2) {
        const results = await Promise.allSettled(chunks.slice(index, index + 2).map(chunk => this.fetchCards(chunk, request.signal)));
        if (request.signal.aborted) return;
        for (const result of results) {
          if (result.status === 'rejected') { failed = true; continue; }
          for (const id of result.value.ids) {
            const card = result.value.cards.get(id);
            if (card) this.cards.set(id, card);
            else this.missing.add(id);
          }
        }
        this.render();
      }
      const saved = window.WohnjoyWishlist.variants();
      const hydrate = ids.filter(id => this.cards.get(id)?.dataset.variantId && /^\d+$/.test(saved[id] || '') && this.cards.get(id).dataset.variantId !== saved[id]);
      for (let index = 0; index < hydrate.length; index += 3) {
        const results = await Promise.allSettled(hydrate.slice(index, index + 3).map(id => this.fetchVariant(id, saved[id], request.signal)));
        if (request.signal.aborted) return;
        for (const result of results) {
          if (result.status === 'fulfilled') {
            const card = result.value;
            this.cards.set(card.dataset.productId, card);
            if (card.dataset.variantId !== saved[card.dataset.productId]) window.WohnjoyWishlist.remember(card.dataset.productId, card.dataset.variantId);
          } else failed = true;
        }
        this.render();
      }
      if (request.signal.aborted) return;
      error.hidden = !failed;
      loader.hidden = true;
      this.list.setAttribute('aria-busy', 'false');
      this.render();
      window.WohnjoyWishlist.sync(window.WohnjoyWishlist.readIds());
    }
    async fetchCards(ids, signal) {
      const url = new URL(window.routes.search_url, location.origin);
      url.searchParams.set('section_id', 'page-wishlist');
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[unavailable_products]', 'last');
      url.searchParams.set('q', ids.map(id => `id:${id}`).join(' OR '));
      const response = await fetch(url, { signal: AbortSignal.any([signal, AbortSignal.timeout(20000)]), credentials: 'same-origin' });
      if (!response.ok) throw new Error('Wishlist unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = doc.querySelector('[data-wj-wishlist-items]');
      if (!list) throw new Error('Wishlist section missing');
      const cards = new Map([...list.querySelectorAll('.wj-wishlist-card[data-product-id]')].filter(card => ids.includes(card.dataset.productId)).map(card => [card.dataset.productId, document.adoptNode(card)]));
      return { ids, cards };
    }
    async fetchVariant(id, variant, signal) {
      const card = this.cards.get(id);
      const url = new URL(card.querySelector('a.product__media').href, location.origin);
      url.searchParams.set('variant', variant);
      url.searchParams.set('section_id', 'wohnjoy-wishlist-card');
      const response = await fetch(url, { signal: AbortSignal.any([signal, AbortSignal.timeout(20000)]), credentials: 'same-origin' });
      if (!response.ok) throw new Error('Saved option unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const selected = doc.querySelector('.wj-wishlist-card[data-product-id]');
      if (!selected || selected.dataset.productId !== id) throw new Error('Saved product missing');
      return document.adoptNode(selected);
    }
    unavailableCard(id) {
      const card = document.createElement('article');
      card.className = 'product-item wj-wishlist-card wj-wishlist-card--unavailable';
      card.dataset.productId = id;
      card.dataset.price = 'Infinity';
      const text = document.createElement('p');
      text.textContent = this.dataset.unavailableLabel;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'wj-wishlist-card__button';
      remove.setAttribute('data-wj-wishlist-remove', '');
      remove.textContent = this.dataset.removeLabel;
      card.append(text, remove);
      return card;
    }
    render() {
      const ids = window.WohnjoyWishlist.readIds();
      const entries = ids.map(id => {
        if (this.missing.has(id) && !this.cards.has(id)) this.cards.set(id, this.unavailableCard(id));
        return this.cards.get(id);
      }).filter(Boolean);
      const mode = this.sort.value;
      entries.sort((first, second) => {
        const firstPrice = Number(first.dataset.price), secondPrice = Number(second.dataset.price);
        if (!Number.isFinite(firstPrice) || !Number.isFinite(secondPrice)) return Number(!Number.isFinite(firstPrice)) - Number(!Number.isFinite(secondPrice)) || ids.indexOf(second.dataset.productId) - ids.indexOf(first.dataset.productId);
        if (mode === 'price-asc' || mode === 'price-desc') return (mode === 'price-asc' ? firstPrice - secondPrice : secondPrice - firstPrice) || ids.indexOf(second.dataset.productId) - ids.indexOf(first.dataset.productId);
        return ids.indexOf(second.dataset.productId) - ids.indexOf(first.dataset.productId);
      });
      const current = [...this.list.children];
      if (current.length !== entries.length || current.some((card, index) => card !== entries[index])) this.list.replaceChildren(...entries);
      const saved = window.WohnjoyWishlist.variants();
      entries.forEach(card => {
        card.setAttribute('role', 'listitem');
        const pending = /^\d+$/.test(saved[card.dataset.productId] || '') && card.dataset.variantId && card.dataset.variantId !== saved[card.dataset.productId];
        card.setAttribute('aria-busy', String(Boolean(pending)));
        const purchase = card.querySelector('button[type="submit"]');
        if (purchase) purchase.disabled = Boolean(pending) || card.dataset.available === 'false';
      });
      this.querySelector('[data-wj-wishlist-toolbar]').hidden = !ids.length;
      this.querySelector('[data-wj-wishlist-empty]').hidden = ids.length > 0;
      const countLabel = ids.length === 1 ? this.dataset.countOne : this.dataset.countLabel;
      this.querySelector('[data-wj-wishlist-count]').textContent = (countLabel || this.dataset.countLabel || '__NUMBER__').replace('__NUMBER__', String(ids.length));
    }
    removeItem(id) {
      const ids = window.WohnjoyWishlist.readIds();
      const index = ids.indexOf(id);
      if (index === -1) return;
      const saved = window.WohnjoyWishlist.setIds(ids.filter(value => value !== id), 'wishlist-page');
      this.errorStorage(!saved);
      if (!saved) return;
      this.removed = { id, index };
      clearTimeout(this.undoTimer);
      const notice = this.querySelector('[data-wj-wishlist-undo]');
      notice.hidden = false;
      this.undoTimer = setTimeout(() => { notice.hidden = true; this.removed = null; }, 10000);
      this.render();
      const focus = this.list.querySelector('[data-wj-wishlist-remove]') || this.querySelector('[data-wj-wishlist-restore]');
      focus?.focus({ preventScroll: true });
    }
    restoreItem() {
      if (!this.removed) return;
      const ids = window.WohnjoyWishlist.readIds();
      if (!ids.includes(this.removed.id)) ids.splice(Math.min(this.removed.index, ids.length), 0, this.removed.id);
      const saved = window.WohnjoyWishlist.setIds(ids, 'wishlist-page');
      this.errorStorage(!saved);
      if (!saved) return;
      const id = this.removed.id;
      clearTimeout(this.undoTimer);
      this.removed = null;
      this.querySelector('[data-wj-wishlist-undo]').hidden = true;
      this.render();
      this.cards.get(id)?.querySelector('[data-wj-wishlist-remove]')?.focus({ preventScroll: true });
    }
  }
  customElements.define('wohnjoy-wishlist-page', WohnjoyWishlistPage);
})();
